// Silmarillion resolve — custom GLSL atmosphere for the Crisis chapter.
// A green storm runs at low intensity throughout the section. When the
// Silmarillion morph orb intersects, the page surrounds the reader with
// the resolution: u_resolve animates 0 → 1, u_intensity boosts, then both
// settle at the resolved blue with low ambient intensity.

import { ShaderCanvas } from './ShaderCanvas';
import silmSrc from './silmarillion.frag';

const STORM_INTENSITY = 0.0;     // ambient before the orb fires
const PEAK_INTENSITY = 0.42;     // gentle wash, not a stamp
const RESTING_INTENSITY = 0.0;   // settles back to nothing

const RESOLVE_MS = 3400;         // unhurried green → blue
const PEAK_HOLD_MS = 1400;       // hold the resolved blue
const FADE_MS = 3200;            // long, graceful fade

export function initSilmarillionResolve(): void {
  const section = document.querySelector<HTMLElement>('.s--crisis');
  const morphOrb = document.getElementById('silmarillion-orb');
  if (!section || !morphOrb) return;


  // Host element. We mount our own (separate from the existing
  // #crisisShader element which hosts the click-orb book palettes).
  const host = document.createElement('div');
  host.className = 'silm-atmosphere';
  host.setAttribute('aria-hidden', 'true');
  host.style.position = 'absolute';
  host.style.inset = '0';
  host.style.zIndex = '0';
  host.style.pointerEvents = 'none';
  host.style.overflow = 'hidden';
  host.style.opacity = '0';
  host.style.transition = 'opacity 1.4s cubic-bezier(0.22, 1, 0.36, 1)';

  if (getComputedStyle(section).position === 'static') {
    section.style.position = 'relative';
  }
  // Insert as the first child so it sits behind everything else.
  section.insertBefore(host, section.firstChild);

  let canvas: ShaderCanvas | null = null;
  let started = false;
  let resolveAnimStart = 0;
  let triggeredResolve = false;

  // ---- Lazy mount on section reveal -----------------------------------
  const sectionIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          canvas = new ShaderCanvas(host, silmSrc, {
            uniforms: {
              u_resolve: 0,
              u_intensity: STORM_INTENSITY,
              u_dark: document.documentElement.dataset.theme === 'dark' ? 1 : 0,
            },
          });
          canvas.start();
          requestAnimationFrame(() => (host.style.opacity = '1'));
          sectionIo.unobserve(e.target);
        }
      });
    },
    { threshold: 0.15 }
  );
  sectionIo.observe(section);

  // ---- Pause when section leaves viewport (save GPU) -------------------
  const visIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (!canvas) return;
        if (e.isIntersecting) canvas.start();
        else canvas.stop();
      });
    },
    { threshold: 0 }
  );
  visIo.observe(section);

  // ---- Resolve trigger: morph orb intersects --------------------------
  const morphIo = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !triggeredResolve && canvas) {
          triggeredResolve = true;
          morphIo.unobserve(e.target);
          // Small delay so the orb's morph animation kicks first.
          setTimeout(() => {
            resolveAnimStart = performance.now();
            requestAnimationFrame(animateResolve);
          }, 600);
        }
      });
    },
    { threshold: 0.6 }
  );
  morphIo.observe(morphOrb);

  // ---- Theme flip --------------------------------------------------
  const themeObs = new MutationObserver(() => {
    canvas?.setUniforms({
      u_dark: document.documentElement.dataset.theme === 'dark' ? 1 : 0,
    });
  });
  themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

  function animateResolve() {
    if (!canvas) return;
    const elapsed = performance.now() - resolveAnimStart;
    let resolve: number;
    let intensity: number;

    if (elapsed < RESOLVE_MS) {
      const t = elapsed / RESOLVE_MS;
      resolve = easeInOutCubic(t);
      intensity = STORM_INTENSITY + (PEAK_INTENSITY - STORM_INTENSITY) * easeOutCubic(t);
    } else if (elapsed < RESOLVE_MS + PEAK_HOLD_MS) {
      resolve = 1;
      intensity = PEAK_INTENSITY;
    } else if (elapsed < RESOLVE_MS + PEAK_HOLD_MS + FADE_MS) {
      const t = (elapsed - RESOLVE_MS - PEAK_HOLD_MS) / FADE_MS;
      resolve = 1;
      intensity = PEAK_INTENSITY + (RESTING_INTENSITY - PEAK_INTENSITY) * easeInOutCubic(t);
    } else {
      // Resolved, resting at subtle ambient blue.
      canvas.setUniforms({ u_resolve: 1, u_intensity: RESTING_INTENSITY });
      return;
    }
    canvas.setUniforms({ u_resolve: resolve, u_intensity: intensity });
    requestAnimationFrame(animateResolve);
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
